import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import {
  adminListUsers, adminCreateUser, adminSetUserActive, adminResetUserMfa, adminResetUserPassword,
  adminListAudit, adminMaintenanceStatus, adminEraseLegacyCopies, adminEraseAbandonedUploads, apiError,
} from "../../lib/api";
import { adminFieldCls } from "./AdminAuth";

const ACTION_LABELS = {
  login: "Signed in", login_failed: "Failed sign-in", mfa_failed: "Failed 2-step code", mfa_enrolled: "Set up 2-step & signed in",
  logout: "Signed out", password_changed: "Changed password", application_viewed: "Viewed application",
  sensitive_revealed: "Revealed protected info", file_downloaded: "Downloaded document", application_deleted: "Deleted application",
  admin_created: "Created admin", admin_activated: "Activated admin", admin_deactivated: "Deactivated admin",
  admin_mfa_reset: "Reset admin 2-step", admin_password_reset: "Reset admin password",
  legacy_copies_erased: "Erased old unencrypted copies", abandoned_uploads_erased: "Erased abandoned uploads", temp_password_expired: "Expired temporary password used",
};

function NewAdminForm({ onCreated }) {
  const [d, setD] = useState({ name: "", email: "", temp_password: "", role: "admin" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await adminCreateUser(d); toast.success("Admin created — share the temporary password privately"); setD({ name: "", email: "", temp_password: "", role: "admin" }); onCreated(); }
    catch (err) { toast.error(apiError(err, "Could not create admin")); } finally { setLoading(false); }
  };
  const field = (k, label, props = {}) => (
    <div>
      <label htmlFor={`new-admin-${k}`} className="block text-xs font-medium text-brand-muted mb-1.5">{label}</label>
      <input id={`new-admin-${k}`} className={adminFieldCls} value={d[k]} onChange={set(k)} required data-testid={`new-admin-${k}`} {...props} />
    </div>
  );
  return (
    <form onSubmit={submit} className="bg-white border border-brand-line rounded-2xl p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="new-admin-form">
      {field("name", "Name")}
      {field("email", "Email", { type: "email", autoComplete: "off" })}
      {field("temp_password", "Temporary password (12+ chars)", { type: "password", autoComplete: "new-password" })}
      <div>
        <label htmlFor="new-admin-role" className="block text-xs font-medium text-brand-muted mb-1.5">Role</label>
        <select id="new-admin-role" className={adminFieldCls} value={d.role} onChange={set("role")} data-testid="new-admin-role">
          <option value="admin">Admin</option><option value="owner">Owner</option>
        </select>
      </div>
      <button type="submit" disabled={loading} data-testid="new-admin-submit"
        className="inline-flex items-center justify-center gap-2 bg-brand-teal text-white rounded-full px-4 py-3 text-sm font-medium hover:bg-brand-tealDark disabled:opacity-60">
        <UserPlus aria-hidden="true" className="w-4 h-4" /> Add admin
      </button>
    </form>
  );
}

export function TeamManager({ me }) {
  const [users, setUsers] = useState([]);
  const load = () => adminListUsers().then(setUsers).catch((e) => toast.error(apiError(e, "Could not load team")));
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPw = (u) => {
    const pw = window.prompt(`New temporary password for ${u.email} (12+ characters, letters and numbers):`);
    if (pw) act(() => adminResetUserPassword(u.id, pw), "Password reset — they must change it at next sign-in");
  };

  return (
    <div className="space-y-5" data-testid="team-manager">
      <p className="text-sm text-brand-muted">Each staff member gets their own login with two-step verification. Owners can manage the team and delete applications.</p>
      <NewAdminForm onCreated={load} />
      <ul className="bg-white border border-brand-line rounded-2xl divide-y divide-brand-line">
        {users.map((u) => (
          <li key={u.id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`team-user-${u.email}`}>
            <div>
              <div className="font-medium text-brand-charcoal">{u.name} <span className="text-xs text-brand-muted font-normal">{u.email}</span></div>
              <div className="text-xs text-brand-muted mt-0.5">
                {u.role} · {u.is_active ? "Active" : "Deactivated"} · 2-step {u.mfa_enabled ? "on" : "not set up"}
                {u.must_change_password && " · must change password"}
              </div>
            </div>
            {u.id !== me.id && (
              <div className="flex gap-2 flex-wrap text-sm">
                <button type="button" onClick={() => resetPw(u)} className="px-3 py-1.5 rounded-full text-brand-teal hover:bg-brand-cream" data-testid={`team-reset-pw-${u.email}`}>Reset password</button>
                <button type="button" onClick={() => act(() => adminResetUserMfa(u.id), "2-step reset")} className="px-3 py-1.5 rounded-full text-brand-teal hover:bg-brand-cream" data-testid={`team-reset-mfa-${u.email}`}>Reset 2-step</button>
                <button type="button" onClick={() => window.confirm(`${u.is_active ? "Deactivate" : "Activate"} ${u.email}?`) && act(() => adminSetUserActive(u.id, !u.is_active), "Updated")}
                  className="px-3 py-1.5 rounded-full text-red-700 hover:bg-red-50" data-testid={`team-toggle-${u.email}`}>
                  {u.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TypedConfirm({ label, description, onConfirm, testid }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onConfirm(text); setOpen(false); setText(""); } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="text-sm text-red-700 border border-red-200 rounded-full px-4 py-2 hover:bg-red-50">{label}</button>
    );
  }
  return (
    <form onSubmit={submit} role="alertdialog" aria-label={label} className="w-full rounded-2xl border border-red-200 bg-red-50 p-4" data-testid={`${testid}-dialog`}>
      <p className="text-sm text-red-800">{description} Type <b className="font-mono">ERASE</b> to confirm.</p>
      <label htmlFor={`${testid}-input`} className="sr-only">Type ERASE to confirm</label>
      <input id={`${testid}-input`} autoFocus value={text} onChange={(e) => setText(e.target.value)}
        className={adminFieldCls + " mt-2 font-mono"} data-testid={`${testid}-input`} />
      <div className="mt-3 flex gap-3 justify-end">
        <button type="button" onClick={() => { setOpen(false); setText(""); }} data-testid={`${testid}-cancel`} className="text-sm text-brand-muted px-3 py-2">Cancel</button>
        <button type="submit" disabled={busy || text.trim().toUpperCase() !== "ERASE"} data-testid={`${testid}-confirm`}
          className="text-sm bg-red-700 text-white rounded-full px-4 py-2 font-medium disabled:opacity-50">{busy ? "Erasing…" : "Erase permanently"}</button>
      </div>
    </form>
  );
}

function StorageMaintenance() {
  const [status, setStatus] = useState(null);
  const load = () => adminMaintenanceStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);
  if (!status) return null;
  const run = (fn, noun) => async (typed) => {
    try { const r = await fn(typed); toast.success(`Erased ${r.erased ?? r.purged ?? 0} ${noun}`); load(); }
    catch (e) { toast.error(apiError(e, "Erase failed")); }
  };
  return (
    <div className="mb-5 bg-white border border-brand-line rounded-2xl p-5 space-y-3" data-testid="storage-maintenance">
      <div className="text-sm">
        <div className="font-medium text-brand-charcoal">Document storage</div>
        <ul className="text-brand-muted mt-1 space-y-0.5">
          <li data-testid="legacy-copies-count">{status.legacy_unencrypted_copies} old copies from before encryption (encrypted copies are kept)</li>
          <li data-testid="abandoned-uploads-count">{status.abandoned_uploads} abandoned uploads (never submitted, older than 48 hours)</li>
          <li>{status.purge_pending} erase(s) pending retry · {status.migration_failed} file(s) failed encryption upgrade</li>
        </ul>
      </div>
      <div className="flex gap-3 flex-wrap">
        {status.legacy_unencrypted_copies > 0 && (
          <TypedConfirm label="Erase old copies" testid="erase-legacy-copies"
            description="This permanently overwrites the old, pre-encryption copies of applicant documents. The encrypted copies stay available."
            onConfirm={run(adminEraseLegacyCopies, "old copies")} />
        )}
        {status.abandoned_uploads > 0 && (
          <TypedConfirm label="Erase abandoned uploads" testid="erase-abandoned-uploads"
            description="This permanently erases documents that were uploaded but never attached to a submitted application."
            onConfirm={run(adminEraseAbandonedUploads, "abandoned uploads")} />
        )}
      </div>
    </div>
  );
}

export function AuditLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { adminListAudit().then(setItems).catch((e) => toast.error(apiError(e, "Could not load activity"))); }, []);
  return (
    <>
    <StorageMaintenance />
    <div className="bg-white border border-brand-line rounded-2xl overflow-x-auto" data-testid="audit-log">
      <table className="w-full text-sm">
        <caption className="sr-only">Admin activity log</caption>
        <thead className="bg-brand-cream">
          <tr className="text-left text-[11px] tracking-widest uppercase text-brand-muted">
            <th scope="col" className="px-5 py-3">When</th><th scope="col" className="px-5 py-3">Who</th>
            <th scope="col" className="px-5 py-3">Action</th><th scope="col" className="px-5 py-3">Record</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-t border-brand-line">
              <td className="px-5 py-3 text-brand-muted whitespace-nowrap">{new Date(a.at).toLocaleString()}</td>
              <td className="px-5 py-3">{a.admin_email || "—"}</td>
              <td className="px-5 py-3">{ACTION_LABELS[a.action] || a.action}</td>
              <td className="px-5 py-3 font-mono text-xs text-brand-muted">{a.target_id ? a.target_id.slice(0, 8).toUpperCase() : "—"}</td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-brand-muted">No activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
    </>
  );
}
